/**
 * Service Afterlife API Client — SRV-FG-4
 * Abandoned equipment, compensation, afterlife decisions, batch worksheets, voluntary surrender.
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export interface AbandonedEquipmentItem {
  rentalId: string;
  rentalNumber: string;
  partnerId: string;
  partnerName: string;
  equipmentName: string;
  equipmentSerialNumber: string | null;
  contractEndDate: string;
  daysSinceEnd: number;
  estimatedStorageFee: number;
  dailyRate: number;
}

export interface AbandonedEquipmentResponse {
  data: AbandonedEquipmentItem[];
  meta: { total: number; page: number; pageSize: number };
}

export interface CompensationPaymentInput {
  rentalId: string;
  amount: number;
  reason: string;
  action: 'WRITTEN_OFF' | 'TRANSFERRED_TO_INVENTORY';
}

export interface CompensationPaymentResponse {
  data: { id: string; status: string; message: string };
}

export interface AfterlifeEquipmentItem {
  id: string;
  name: string;
  serialNumber: string | null;
  category: 'NEEDS_SCRAP' | 'NEEDS_REPAIR' | 'NEEDS_DECISION';
  conditionScore: number;
  ageMonths: number;
  repairCount: number;
  lastServiceDate: string | null;
  suggestedAction: string;
  worksheetId: string | null;
}

export interface AfterlifeDecisionsResponse {
  data: {
    needsScrap: AfterlifeEquipmentItem[];
    needsRepair: AfterlifeEquipmentItem[];
    needsDecision: AfterlifeEquipmentItem[];
    summary: { scrapCount: number; repairCount: number; decisionCount: number; total: number };
  };
}

export interface BatchCreateInput {
  equipmentBarcodes: string[];
  type: 'FIZETOS' | 'GARANCIALIS' | 'BERLESI' | 'KARBANTARTAS';
  assignedToId?: string;
}

export interface BatchCreateResponse {
  data: {
    created: Array<{ barcode: string; worksheetId: string; worksheetNumber: string }>;
    failures: Array<{ barcode: string; reason: string }>;
  };
}

export interface VoluntarySurrenderInput {
  rentalId: string;
  statement: string;
}

export interface VoluntarySurrenderResponse {
  data: {
    rentalId: string;
    status: string;
    worksheetId: string | null;
    message: string;
  };
}

// ============================================
// API FUNCTIONS
// ============================================

/** SRV-FG-4-1: List abandoned equipment overdue for return */
export async function getAbandonedEquipment(
  page = 1,
  pageSize = 20
): Promise<AbandonedEquipmentResponse> {
  return api.get<AbandonedEquipmentResponse>(
    `/service/abandoned-equipment?page=${page}&pageSize=${pageSize}`
  );
}

/** SRV-FG-4-2: Record compensation payment for damaged/lost equipment */
export async function recordCompensationPayment(
  input: CompensationPaymentInput
): Promise<CompensationPaymentResponse> {
  return api.post<CompensationPaymentResponse>('/service/compensation-payment', input);
}

/** SRV-FG-4-3: Get afterlife decisions dashboard data */
export async function getAfterlifeDecisions(
  category?: string
): Promise<AfterlifeDecisionsResponse> {
  const params = category ? `?category=${category}` : '';
  return api.get<AfterlifeDecisionsResponse>(`/service/afterlife-decisions${params}`);
}

/** SRV-FG-4-4: Batch create worksheets from barcodes */
export async function batchCreateWorksheets(input: BatchCreateInput): Promise<BatchCreateResponse> {
  return api.post<BatchCreateResponse>('/service/worksheets/batch-create', input);
}

/** SRV-FG-4-5: Record voluntary equipment surrender */
export async function recordVoluntarySurrender(
  input: VoluntarySurrenderInput
): Promise<VoluntarySurrenderResponse> {
  return api.post<VoluntarySurrenderResponse>('/service/voluntary-surrender', input);
}
